// src/pages/Terms.tsx
import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';

export default function Terms(): JSX.Element {
  return (
    <Box 
      sx={{ 
        fontFamily: 'sans-serif', 
        lineHeight: 1.6, 
        p: { xs: 2, sm: 3, md: 4 }, 
        maxWidth: '800px', 
        mx: 'auto' 
      }}
    >
      <Helmet>
        <title>Terms and Conditions</title>
      </Helmet>

      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        TERMS AND CONDITIONS
      </Typography>

      <Box sx={{ bgcolor: 'grey.100', p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="body2" paragraph>
          <strong>Version:</strong> 1.0 — Latest update: March 4, 2026
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Legal entity:</strong> CityWise
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Org.nr:</strong> 920104-7090
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Address:</strong> Vinstavägen 36, 162 70, Sweden
        </Typography>
        <Typography variant="body2">
          <strong>Contact:</strong> <a href="mailto:contact@citywisestrategy.com">contact@citywisestrategy.com</a>
        </Typography>
      </Box>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Introduction
      </Typography>
      <Typography variant="body1" paragraph>
        CityWise (reg. no. 920104-7090) ("CityWise," "we," "us") operates a digital decision support platform designed to help municipal planners simulate hazard impacts, identify hidden vulnerabilities, and meet climate adaptation requirements. Our services include:
      </Typography>
      <ul>
        <li><Typography variant="body1">The website www.citywisestrategy.com and related subdomains (the "Site")</Typography></li>
        <li><Typography variant="body1">The CityWise web-based platform for managing cascading infrastructure risks</Typography></li>
        <li><Typography variant="body1">Any related digital tools, features, content, or services linked to these Terms (collectively, the "Platform")</Typography></li>
      </ul>
      <Typography variant="body1" paragraph>
        These Terms and Conditions ("Terms") form a legally binding agreement between you—whether as an individual or representing a municipality, company, or other entity—and CityWise. By creating an account, accessing, or using the Platform, you confirm that you have read, understood, and agreed to be bound by these Terms and our Privacy Policy. If you do not agree, you must not use the Platform.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        1. General
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>1.1</strong> These Terms apply to all registered Users ("User", "You") who access or use the Platform. The Platform is available only if you accept all provisions of these Terms and the Privacy Policy.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>1.2</strong> You are responsible for ensuring that your use of the Platform complies with all applicable laws and regulations in your jurisdiction.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>1.3</strong> We reserve the right to update, modify, or discontinue parts of the Platform at any time, with or without notice.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>1.4</strong> By using the Platform, you acknowledge and agree to our Privacy Policy, which describes how we collect, use, and protect personal data.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        2. What is CityWise?
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>2.1</strong> CityWise is a digital platform that automates cascading failure analysis for critical infrastructure. We provide municipal planners and engineers with tools to visualize spatial dependencies, simulate long-term climate threats, and formulate adaptation strategies. Through the Platform, Users can access:
      </Typography>
      <ul>
        <li><Typography variant="body1">Automated tools for hazard risk mapping and spatial exposure mapping.</Typography></li>
        <li><Typography variant="body1">Infrastructure modeling to detect directly and indirectly disrupted assets.</Typography></li>
        <li><Typography variant="body1">Contingency planning dashboards to evaluate structural vulnerabilities and impacts.</Typography></li>
      </ul>
      <Typography variant="body1" paragraph>
        <strong>2.2</strong> Any reliance on the simulations, mapping, or advice generated by the Platform is at the User's discretion. The User remains responsible for verifying the accuracy, completeness, and applicability of any data or output. CityWise's role is limited to providing decision support tools; CityWise does not guarantee the prevention of actual infrastructure failures, as final municipal planning and engineering decisions rest with the relevant local authorities and planners.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        3. The Platform and User Account
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>3.2</strong> To access the Platform, you must have a User Account. Your account is personal, non-transferable, and you are fully responsible for all activity under your account under these Terms. You agree to maintain the confidentiality of your credentials and to notify us immediately of any unauthorized use.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>3.3</strong> By providing your email address, you consent to receive Platform-related notices, updates, and legally required communications from CityWise.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        4. Prohibited Use and Misuse of the Platform
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>4.1</strong> You agree to use the Platform solely for its intended purposes. You are expressly prohibited from:
      </Typography>
      <ul>
        <li><Typography variant="body1">Submitting false or malicious infrastructure/meteorological data to the Platform.</Typography></li>
        <li><Typography variant="body1">Attempting to gain unauthorized access, reverse engineer, or bypass security measures.</Typography></li>
        <li><Typography variant="body1">Introducing malware, scraping tools, or automated bots intended to disrupt the Platform.</Typography></li>
        <li><Typography variant="body1">Using the Platform to violate any applicable data protection or infrastructural security laws.</Typography></li>
      </ul>
      <Typography variant="body1" paragraph>
        <strong>4.2</strong> CityWise reserves the right to suspend or terminate accounts engaging in misuse, and to hold you liable to indemnify CityWise for any damages or legal fees arising from your breach of these Terms.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        5. Limitations of Liability
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>5.1 "As-Is" Basis:</strong> The Platform is offered "as-is" and "as-available." While we strive to provide accurate modeling and simulations, we disclaim any implied warranties of completeness, absolute correctness, or fitness for a particular emergency response scenario.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>5.2 Third-Party Data:</strong> Simulations are based heavily on third-party meteorological, spatial, and governmental data. CityWise expressly disclaims any liability for inaccuracies in third-party data or the physical real-world outcomes of extreme weather events.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>5.3 Exclusion of Indirect Damages:</strong> To the maximum extent permitted by law, CityWise disclaims all liability for indirect, incidental, special, or consequential damages, including loss of data, lost revenue, or specific physical infrastructure damages.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>5.4 Liability Cap:</strong> In no event shall CityWise's aggregate liability exceed the total fees paid by you to CityWise in the twelve (12) months preceding the event giving rise to the claim (subject to mandatory Swedish law provisions, e.g., intentional misconduct).
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        6. Accessibility to the Platform
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>6.1</strong> CityWise strives to maintain high availability, but does not guarantee uninterrupted or error-free access. We are not liable for service interruptions caused by third-party hosting providers, internet outages, or scheduled maintenance.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        7. Intellectual Property
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>7.1 Ownership:</strong> All intellectual property rights in the Platform, including underlying simulation algorithms, design, trademarks, and documentation, are the exclusive property of CityWise.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>7.2 License to Use:</strong> We grant you a limited, non-exclusive, non-sublicensable license to use the Platform for your municipal or organizational planning purposes during your subscription term. You may not copy, reverse engineer, or commercially resell the Platform's core algorithms.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>7.3 User-Provided Materials:</strong> Any specific infrastructural data or GIS layers you upload remain your property. You grant CityWise a license to host and process this data as necessary to perform the simulation services.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        8. Termination of Agreement
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>8.1</strong> This Agreement remains in force until terminated by either party.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>8.2</strong> CityWise reserves the right to suspend or terminate your access with immediate effect if you breach these Terms, pose a security risk, or if required by law.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>8.3</strong> Upon termination, you must cease all use of the Platform. Retention of account data post-termination will be handled in accordance with our Privacy Policy and Swedish accounting/recordkeeping laws.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        9. Ethics, Values, and Applicable Law
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>9.1</strong> CityWise operates in strict compliance with applicable Swedish and EU laws. We maintain a zero-tolerance policy toward bribery, corruption, and the misuse of sensitive infrastructural data.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>9.2</strong> We are committed to transparency, accurate representation of our simulation capabilities, and respectful engagement with all stakeholders and municipal partners.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        10. Changes to These Terms
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>10.1</strong> CityWise reserves the right to modify these Terms. Material changes will be communicated with reasonable advance notice (e.g., via email or in-Platform notifications). Your continued use of the Platform following the effective date constitutes acceptance under Swedish contract law (Avtalslagen [1915:218]).
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Contact
      </Typography>
      <Typography variant="body1" paragraph>
        For questions, comments, or formal legal notices regarding these Terms, you may contact us by email at <a href="mailto:contact@citywisestrategy.com">contact@citywisestrategy.com</a> or in writing to:
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>CityWise</strong><br />
        Org. No: 920104-7090<br />
        Vinstavägen 36<br />
        162 70, Sweden
      </Typography>
    </Box>
  );
}